import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Wallet, Plus, Clock, ArrowRight, X, Image as ImageIcon, History, ChevronLeft, CreditCard, Landmark, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "https://bvglinkamazon.com") + "/api";

export const BVGPayWallet = () => {
    const navigate = useNavigate();
    const token = localStorage.getItem("buyer_token");
    const [loading, setLoading] = useState(true);
    const [walletData, setWalletData] = useState({ balance_htg: 0, transactions: [], topups: [] });
    const [showTopupModal, setShowTopupModal] = useState(false);
    
    // Topup Form State
    const [amount, setAmount] = useState("");
    const [method, setMethod] = useState("moncash_direct");
    const [reference, setReference] = useState("");
    const [proofImage, setProofImage] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!token) {
            toast.error("Tanpri konekte oswa kreye yon kont pou w wè Bous ou.");
            navigate("/linkamazon/login");
            return;
        }
        fetchWallet();
    }, [token, navigate]);

    const fetchWallet = async () => {
        try {
            const res = await axios.get(`${API}/linkamazon/bvgpay/wallet`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setWalletData(res.data);
        } catch (error) {
            console.error(error);
            toast.error("Erè nan chaje Bous ou a.");
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            toast.error("Foto a twò gwo. (Max 5MB)");
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            setProofImage(reader.result); // Base64 proof
        };
        reader.readAsDataURL(file);
    };

    const submitTopup = async (e) => {
        e.preventDefault();
        const parsedAmount = parseFloat(amount) || 0;
        const finalAmountHTG = method === 'zelle' ? parsedAmount * 131 : parsedAmount;
        
        if (finalAmountHTG <= 0) return toast.error("Kantite a pa valid.");
        if (!reference) return toast.error("Tanpri mete nimewo referans lan.");

        setIsSubmitting(true);
        try {
            await axios.post(`${API}/linkamazon/bvgpay/topup`, {
                amount_htg: finalAmountHTG,
                method,
                reference_id: reference,
                proof_image: proofImage
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            toast.success("Demann rechaj la voye! N'ap verifyel trè byento.");
            setShowTopupModal(false);
            setAmount("");
            setReference("");
            setProofImage(null);
            fetchWallet();
        } catch (error) {
            toast.error("Echèk nan voye demann lan.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case "approved": return <span className="bg-green-100 text-green-700 px-2 py-1 rounded-md text-[10px] uppercase font-black">Apwouve</span>;
            case "pending": return <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded-md text-[10px] uppercase font-black">An Atant</span>;
            case "rejected": return <span className="bg-red-100 text-red-700 px-2 py-1 rounded-md text-[10px] uppercase font-black">Rejte</span>;
            default: return <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded-md text-[10px] uppercase font-black">{status}</span>;
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col font-sans">
            <Header />

            <main className="flex-1 container mx-auto px-4 py-8 lg:py-12 max-w-4xl">
                <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6 font-bold text-gray-400 hover:text-gray-900">
                    <ChevronLeft className="w-4 h-4 mr-2" /> Tounen
                </Button>

                {loading ? (
                    <div className="flex justify-center py-20"><div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>
                ) : (
                    <div className="space-y-8">
                        {/* WALLET CARD */}
                        <div className="relative bg-gradient-to-br from-indigo-600 via-indigo-700 to-indigo-900 rounded-[40px] p-8 lg:p-12 overflow-hidden shadow-2xl text-white">
                            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
                            
                            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
                                <div className="space-y-2 text-center md:text-left">
                                    <div className="flex items-center gap-3 justify-center md:justify-start opacity-80 uppercase tracking-widest text-xs font-black">
                                        <Wallet className="w-5 h-5" />
                                        Bous BVGPAY
                                    </div>
                                    <h1 className="text-5xl lg:text-7xl font-black tracking-tighter italic">
                                        {walletData.balance_htg.toLocaleString()} <span className="text-2xl lg:text-4xl text-indigo-300 not-italic">HTG</span>
                                    </h1>
                                    <p className="text-indigo-200 text-sm font-bold">Lajan ou an sekirite epi pare pou achte!</p>
                                </div>

                                <Button 
                                    onClick={() => setShowTopupModal(true)}
                                    className="bg-white text-indigo-900 hover:bg-gray-100 font-black uppercase tracking-wider h-16 px-8 rounded-2xl shadow-xl hover:scale-105 transition-all text-sm w-full md:w-auto mt-4 md:mt-0"
                                >
                                    <Plus className="w-6 h-6 mr-2" /> Rechaje Bous an
                                </Button>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {/* TOPUPS PENDING */}
                            <div className="bg-white rounded-[30px] p-6 lg:p-8 shadow-sm border border-gray-100">
                                <div className="flex items-center justify-between mb-6">
                                    <h3 className="text-lg font-black uppercase tracking-wider text-gray-900 flex items-center gap-2">
                                        <Clock className="w-5 h-5 text-orange-500" /> Demann Rechaj
                                    </h3>
                                </div>
                                <div className="space-y-4 max-h-[300px] overflow-y-auto custom-scrollbar pr-2">
                                    {walletData.topups.length === 0 ? (
                                        <div className="text-center py-8 text-gray-400 font-bold text-sm">Ou pa gen okenn demann rechaj ki anrejistre.</div>
                                    ) : (
                                        walletData.topups.map((t, i) => (
                                            <div key={i} className="flex items-center justify-between p-4 rounded-2xl bg-gray-50 hover:bg-gray-100 transition-colors">
                                                <div>
                                                    <p className="font-black text-gray-900">{t.amount_htg.toLocaleString()} HTG</p>
                                                    <p className="text-[10px] text-gray-400 font-bold uppercase">{t.method.replace('_', ' ')} • Ref: {t.reference_id}</p>
                                                    <p className="text-[9px] text-gray-400">{new Date(t.created_at).toLocaleDateString()}</p>
                                                </div>
                                                {getStatusBadge(t.status)}
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>

                            {/* TRANSACTIONS HISTORY */}
                            <div className="bg-white rounded-[30px] p-6 lg:p-8 shadow-sm border border-gray-100">
                                <div className="flex items-center justify-between mb-6">
                                    <h3 className="text-lg font-black uppercase tracking-wider text-gray-900 flex items-center gap-2">
                                        <History className="w-5 h-5 text-indigo-500" /> Istwa Bous la
                                    </h3>
                                </div>
                                <div className="space-y-4 max-h-[300px] overflow-y-auto custom-scrollbar pr-2">
                                    {walletData.transactions.length === 0 ? (
                                        <div className="text-center py-8 text-gray-400 font-bold text-sm">Poko gen okenn tranzaksyon.</div>
                                    ) : (
                                        walletData.transactions.map((tx, i) => (
                                            <div key={i} className="flex items-center justify-between p-4 rounded-2xl bg-gray-50 hover:bg-gray-100 transition-colors">
                                                <div className="flex-1 min-w-0 pr-4">
                                                    <p className="font-bold text-sm text-gray-900 truncate">{tx.description}</p>
                                                    <p className="text-[10px] text-gray-400 font-bold uppercase mt-1">{new Date(tx.created_at).toLocaleDateString()}</p>
                                                </div>
                                                <div className={`font-black whitespace-nowrap ${tx.amount > 0 ? 'text-green-600' : 'text-gray-900'}`}>
                                                    {tx.amount > 0 ? '+' : ''}{tx.amount.toLocaleString()} HTG
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* INFO & ADVANTAGES */}
                        <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-[30px] p-8 border border-orange-200">
                            <h3 className="font-black text-orange-800 text-xl uppercase tracking-widest mb-4">Poukisa peye ak BVGPAY?</h3>
                            <ul className="space-y-3 font-bold text-sm text-orange-900/80">
                                <li className="flex items-center gap-2">👉 Plis Sekirite: Pa gen risk blòk MonCash.</li>
                                <li className="flex items-center gap-2">👉 Gwo Rabè: <b>Paye ak BVGPAY e w'ap jwenn jiskaske 3% Rabè sou total peman asistan'w lan!</b> (Se 5% w'ap peye olye 8%)</li>
                                <li className="flex items-center gap-2">👉 Rapid: Vant yo fèt la pou la san tann konfimasyon.</li>
                            </ul>
                        </div>
                    </div>
                )}
            </main>

            {/* TOPUP MODAL */}
            <Dialog open={showTopupModal} onOpenChange={setShowTopupModal}>
                <DialogContent className="w-[95vw] max-h-[95vh] sm:w-full sm:max-w-[450px] rounded-[30px] md:rounded-[40px] p-0 overflow-hidden bg-white border-none shadow-2xl flex flex-col">
                    <DialogHeader className="p-8 pb-4 bg-indigo-50 border-b border-indigo-100">
                        <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center text-white mb-4 shadow-md">
                            <Wallet className="w-6 h-6" />
                        </div>
                        <DialogTitle className="text-2xl font-black italic tracking-tighter uppercase leading-none text-indigo-900">
                            Rechaje Bous Ou
                        </DialogTitle>
                        <DialogDescription className="text-indigo-600 font-bold text-[10px] uppercase tracking-widest mt-2">
                            Achte sa w vle rapid
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={submitTopup} className="p-5 md:p-8 space-y-5 md:space-y-6 overflow-y-auto custom-scrollbar flex-1">
                        {/* Method Selection First so Amount changes context */}
                        <div className="space-y-3">
                            <Label className="text-[10px] font-black uppercase tracking-widest text-gray-500">Mwayen Peman</Label>
                            <div className="grid grid-cols-2 gap-3">
                                {[
                                    { id: 'moncash_direct', label: 'MonCash', icon: Phone },
                                    { id: 'virement', label: 'Bank Nat.', icon: Landmark },
                                    { id: 'zelle', label: 'Zelle', icon: CreditCard },
                                    { id: 'natcash', label: 'Natcash', icon: Phone }
                                ].map(opt => (
                                    <button
                                        type="button"
                                        key={opt.id}
                                        onClick={() => { setMethod(opt.id); setAmount(""); }}
                                        className={`flex items-center justify-center gap-2 h-12 rounded-xl text-[10px] font-black uppercase tracking-wider border-2 transition-all ${
                                            method === opt.id ? 'border-indigo-600 bg-indigo-50 text-indigo-600' : 'border-gray-100 bg-white text-gray-400 hover:border-gray-200'
                                        }`}
                                    >
                                        <opt.icon className="w-4 h-4" /> {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="space-y-3">
                            <div className="flex justify-between items-center">
                                <Label className="text-[10px] font-black uppercase tracking-widest text-gray-500">
                                    Kantite Lajan ({method === 'zelle' ? 'USD $' : 'HTG'})
                                </Label>
                                {method === 'zelle' && amount && (
                                    <span className="text-[9px] font-black uppercase tracking-widest text-green-600 bg-green-50 px-2 py-1 rounded">
                                        Egal: {((parseFloat(amount) || 0) * 131).toLocaleString()} HTG
                                    </span>
                                )}
                            </div>
                            <Input
                                type="number"
                                placeholder={method === 'zelle' ? "Egz: 50 (l'ap ba ou 6,550 HTG)" : "Egz: 5000"}
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                required
                                className="h-14 rounded-2xl bg-gray-50 border-gray-200 text-lg font-black px-6"
                            />
                        </div>

                        {/* Instructions Based on Method */}
                        <div className="bg-blue-50 text-blue-800 p-4 rounded-xl text-[11px] font-bold border border-blue-100">
                            {method === 'moncash_direct' && <p>Tanpri voye kòb la sou MonCash sa: <strong className="text-blue-900 text-sm block mt-1">3XXX-XXXX</strong> (Non: BVGLINK).</p>}
                            {method === 'zelle' && <p>Fè peman Zelle sou adrès sa: <strong className="text-blue-900 text-[13px] block mt-1">2038647557</strong> <span className="block mt-2 font-black text-green-700 bg-green-100 px-2 py-1 rounded inline-block">1 USD = 131 HTG</span></p>}
                            {method === 'virement' && <p>Vireman Bank Nasyonal, Kont: <strong className="text-blue-900 text-sm block mt-1">2801104098 (Sogebank)</strong></p>}
                            {method === 'natcash' && <p>Voyel sou Natcash sa: <strong className="text-blue-900 text-sm block mt-1">4XXX-XXXX</strong></p>}
                            <p className="mt-2 text-blue-600 italic border-t border-blue-200/50 pt-2 text-[10px]">Epi verifye byen anvan ou antre nimewo referans (Transaction ID) la anba la a.</p>
                        </div>

                        <div className="space-y-3">
                            <Label className="text-[10px] font-black uppercase tracking-widest text-gray-500">ID Referans / TransID</Label>
                            <Input
                                placeholder="Egz: 1045982..."
                                value={reference}
                                onChange={(e) => setReference(e.target.value)}
                                required
                                className="h-12 rounded-xl bg-gray-50 border-gray-200 text-sm font-bold px-4"
                            />
                        </div>

                        <div className="space-y-3">
                            <Label className="text-[10px] font-black uppercase tracking-widest text-gray-500 flex items-center justify-between">
                                Prèv (Screenshout)
                                {proofImage && <span className="text-green-500 lowercase tracking-normal">foto chaje ✔</span>}
                            </Label>
                            <div className="relative h-12 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-center cursor-pointer">
                                <input type="file" accept="image/*" onChange={handleFileUpload} className="absolute inset-0 opacity-0 z-10 cursor-pointer" />
                                <div className="text-xs font-bold text-gray-400 flex items-center gap-2"><ImageIcon className="w-4 h-4" /> {proofImage ? "Chanje Imaj" : "Klike pou mete ScreenShot"}</div>
                            </div>
                        </div>

                        <Button 
                            type="submit" 
                            disabled={isSubmitting} 
                            className="w-full h-14 bg-indigo-600 hover:bg-indigo-700 text-white font-black text-sm uppercase tracking-widest rounded-2xl shadow-xl hover:shadow-indigo-500/30 transition-all mt-4"
                        >
                            {isSubmitting ? "Apt tann..." : "Voye Demann lan"}
                        </Button>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};
